import { TIPOS_MOVIMIENTO_STOCK } from '../../constants/tiposMovimientoStock';
import type { ConsultaAnalizarRequest, FiltrosConsultaIa, ModuloConsultaIa } from '../../types/consultasIa';

export interface OpcionFiltroIa {
    value: string;
    label: string;
}

export interface CampoFiltroIa {
    clave: keyof ConsultaAnalizarRequest & string;
    etiqueta: string;
    tipo: 'texto' | 'numero' | 'select';
    opciones?: OpcionFiltroIa[];
    /** Texto de la opción vacía en selects. */
    etiquetaVacia?: string;
    ayuda?: string;
}

export interface AlcanceIa {
    valor: string;
    etiqueta: string;
    campos: CampoFiltroIa[];
}

export interface ConfigModuloIa {
    /** Ej.: "tus ventas", "tu inventario". */
    objeto: string;
    sugerencias: string[];
    alcance?: {
        clave: 'subModuloInventario' | 'subModuloTesoreria';
        etiqueta: string;
        opciones: AlcanceIa[];
    };
    campos: CampoFiltroIa[];
}

const ASENTADO: CampoFiltroIa = { clave: 'asentado', etiqueta: 'Asentado', tipo: 'texto' };
const CODIGO_CAJA: CampoFiltroIa = { clave: 'codigoCaja', etiqueta: 'Código caja', tipo: 'numero' };
const NRO_SUCURSAL: CampoFiltroIa = { clave: 'nroSuc', etiqueta: 'Nro. sucursal', tipo: 'numero' };

export const CONFIG_MODULOS_IA: Record<ModuloConsultaIa, ConfigModuloIa> = {
    ventas: {
        objeto: 'tus ventas',
        sugerencias: [
            '¿Cuáles fueron los clientes con mayor facturación en el período?',
            '¿Qué vendedor tuvo más ventas y por qué monto?',
            'Resumime el total vendido por mes',
            '¿Cuántos comprobantes hay sin asentar?',
        ],
        campos: [
            { clave: 'nroCliente', etiqueta: 'Nro. Cliente', tipo: 'texto' },
            { clave: 'nroVendedor', etiqueta: 'Nro. Vendedor', tipo: 'texto' },
            ASENTADO,
        ],
    },
    compras: {
        objeto: 'tus compras',
        sugerencias: [
            '¿Cuáles son los proveedores con mayor volumen de compras?',
            'Resumime el total comprado por mes',
            '¿Qué comprobantes de compra están sin asentar?',
            '¿Cuáles fueron las compras de mayor importe del período?',
        ],
        campos: [
            { clave: 'nroProveedor', etiqueta: 'Nro. Proveedor', tipo: 'texto' },
            ASENTADO,
            { clave: 'searchTerm', etiqueta: 'Búsqueda', tipo: 'texto', ayuda: 'Nombre, nro. comprobante...' },
        ],
    },
    inventario: {
        objeto: 'tu inventario',
        sugerencias: [
            'Resumime los ajustes de inventario del período',
            '¿Qué artículos tuvieron más movimientos de stock?',
            '¿Qué depósitos registraron más egresos?',
            '¿Hubo transferencias entre depósitos? Detallalas',
        ],
        alcance: {
            clave: 'subModuloInventario',
            etiqueta: 'Alcance inventario',
            opciones: [
                {
                    valor: 'ajustes',
                    etiqueta: 'Ajustes y transferencias (formularios)',
                    campos: [
                        {
                            clave: 'searchTerm',
                            etiqueta: 'Búsqueda',
                            tipo: 'texto',
                            ayuda: 'Usuario, nombre, CUIT, nro. formulario...',
                        },
                    ],
                },
                {
                    valor: 'movimientos-stock',
                    etiqueta: 'Movimientos de stock',
                    campos: [
                        { clave: 'codigoArticulo', etiqueta: 'Código artículo', tipo: 'texto' },
                        {
                            clave: 'tipoMovimientoStock',
                            etiqueta: 'Tipo movimiento',
                            tipo: 'select',
                            etiquetaVacia: 'Todos',
                            opciones: Object.entries(TIPOS_MOVIMIENTO_STOCK).map(([k, label]) => ({
                                value: k,
                                label: `${k} — ${label}`,
                            })),
                        },
                        { clave: 'deposito', etiqueta: 'Nro. depósito', tipo: 'numero' },
                    ],
                },
            ],
        },
        campos: [],
    },
    tesoreria: {
        objeto: 'tu tesorería',
        sugerencias: [
            '¿Cuál fue el total de ingresos y egresos de caja?',
            'Resumime los movimientos por medio de cobro',
            '¿Qué cajas tuvieron más movimientos?',
            '¿Cuántas transferencias entre cajas hubo en el período?',
        ],
        alcance: {
            clave: 'subModuloTesoreria',
            etiqueta: 'Alcance tesorería',
            opciones: [
                {
                    valor: 'planilla-caja',
                    etiqueta: 'Planilla de caja',
                    campos: [
                        { clave: 'searchTerm', etiqueta: 'Búsqueda', tipo: 'texto' },
                        CODIGO_CAJA,
                        {
                            clave: 'tipoMovimientoCaja',
                            etiqueta: 'Tipo (caja)',
                            tipo: 'select',
                            etiquetaVacia: 'Todos',
                            opciones: [
                                { value: 'I', label: 'Ingreso (I)' },
                                { value: 'E', label: 'Egreso (E)' },
                            ],
                        },
                        { clave: 'medioCobro', etiqueta: 'Medio cobro', tipo: 'texto' },
                    ],
                },
                {
                    valor: 'bancarios',
                    etiqueta: 'Movimientos bancarios',
                    campos: [
                        {
                            clave: 'tipoMovimientoBancario',
                            etiqueta: 'Tipo bancario',
                            tipo: 'select',
                            etiquetaVacia: '55 y 56',
                            opciones: [
                                { value: '55', label: '55 Depósito' },
                                { value: '56', label: '56 Extracción' },
                            ],
                        },
                        CODIGO_CAJA,
                        NRO_SUCURSAL,
                    ],
                },
                {
                    valor: 'varios',
                    etiqueta: 'Movimientos varios',
                    campos: [
                        {
                            clave: 'tipoMovimientoVario',
                            etiqueta: 'Tipo varios',
                            tipo: 'select',
                            etiquetaVacia: '5 y 7',
                            opciones: [
                                { value: '5', label: '5 Ingreso VS' },
                                { value: '7', label: '7 Egreso VS' },
                            ],
                        },
                        CODIGO_CAJA,
                        NRO_SUCURSAL,
                    ],
                },
                {
                    valor: 'transferencias',
                    etiqueta: 'Transferencias entre cajas',
                    campos: [
                        { clave: 'codigoCajaOrigen', etiqueta: 'Caja origen', tipo: 'numero' },
                        { clave: 'codigoCajaDestino', etiqueta: 'Caja destino', tipo: 'numero' },
                        NRO_SUCURSAL,
                    ],
                },
            ],
        },
        campos: [],
    },
};

/** Campos de filtro aplicables según el módulo y el alcance elegido en los filtros. */
export const camposActivos = (config: ConfigModuloIa, filtros: FiltrosConsultaIa): CampoFiltroIa[] => {
    if (!config.alcance) return config.campos;
    const valor = filtros[config.alcance.clave] || config.alcance.opciones[0].valor;
    const alcance = config.alcance.opciones.find((o) => o.valor === valor) ?? config.alcance.opciones[0];
    return [...config.campos, ...alcance.campos];
};

export const etiquetaAlcance = (config: ConfigModuloIa, filtros: FiltrosConsultaIa): string | undefined => {
    if (!config.alcance) return undefined;
    const valor = filtros[config.alcance.clave] || config.alcance.opciones[0].valor;
    return config.alcance.opciones.find((o) => o.valor === valor)?.etiqueta;
};

export const valorVisible = (campo: CampoFiltroIa, valor: string): string =>
    campo.tipo === 'select' ? (campo.opciones?.find((o) => o.value === valor)?.label ?? valor) : valor;

export const filtrosIniciales = (config: ConfigModuloIa, fechaDesde: string, fechaHasta: string): FiltrosConsultaIa => {
    const base: FiltrosConsultaIa = { fechaDesde, fechaHasta };
    if (config.alcance) base[config.alcance.clave] = config.alcance.opciones[0].valor;
    return base;
};

const aEntero = (s: string): number | undefined => {
    const t = s.trim();
    if (!t) return undefined;
    const n = parseInt(t, 10);
    return Number.isFinite(n) ? n : undefined;
};

export const construirPayload = (
    modulo: ModuloConsultaIa,
    config: ConfigModuloIa,
    filtros: FiltrosConsultaIa,
): Omit<ConsultaAnalizarRequest, 'pregunta' | 'conversacionId'> => {
    const payload: Record<string, unknown> = {
        modulo,
        fechaDesde: filtros.fechaDesde || undefined,
        fechaHasta: filtros.fechaHasta || undefined,
    };
    if (config.alcance) {
        payload[config.alcance.clave] = filtros[config.alcance.clave] || config.alcance.opciones[0].valor;
    }
    camposActivos(config, filtros).forEach((c) => {
        const v = (filtros[c.clave] ?? '').trim();
        payload[c.clave] = c.tipo === 'numero' ? aEntero(v) : v || undefined;
    });
    return payload as Omit<ConsultaAnalizarRequest, 'pregunta' | 'conversacionId'>;
};
